import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Gift,
  Copy,
  Check,
  Share2,
  Users,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  Percent,
} from "lucide-react";

interface ReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ReferralModal: React.FC<ReferralModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  // Generar código único y predecible basado en el usuario
  const referralCode =
    user?.user_metadata?.referral_code ||
    (user?.email
      ? user.email.split("@")[0].toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8) +
        "-" +
        user.id.slice(0, 4).toUpperCase()
      : user?.id?.slice(0, 8).toUpperCase() || "MIA-AMIGO");

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://app.mialegal.co";
  const referralLink = `${baseUrl}/signup?ref=${referralCode}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("¡Enlace de referido copiado al portapapeles!");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error("No se pudo copiar el enlace automáticamente.");
    }
  };

  const handleShareWhatsApp = () => {
    const message = `¡Hola! Te recomiendo MiA, la plataforma de Inteligencia Artificial para el ejercicio del derecho en Colombia (revisión de contratos, demandas, jurisprudencia y control de expedientes). ⚖️✨\n\nRegístrate con mi enlace de referido para comenzar: ${referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto p-0 border-0 rounded-3xl shadow-2xl bg-white">
        {/* Header Hero */}
        <div className="relative p-6 sm:p-8 bg-gradient-to-br from-indigo-900 via-indigo-800 to-violet-900 text-white rounded-t-3xl overflow-hidden">
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-violet-500/20 rounded-full blur-2xl pointer-events-none" />
          <div className="absolute bottom-0 right-6 opacity-10 pointer-events-none">
            <Gift className="w-40 h-40 text-white" />
          </div>

          <div className="relative z-10 flex items-center gap-2 mb-3">
            <Badge className="bg-amber-400 text-slate-950 font-black text-xs px-3 py-1 uppercase tracking-wider flex items-center gap-1.5 border-none shadow-sm">
              <Sparkles className="w-3.5 h-3.5" />
              Programa de Referidos
            </Badge>
            <Badge className="bg-white/10 text-white text-xs border-white/20 backdrop-blur-sm">
              Sin Límites
            </Badge>
          </div>

          <DialogTitle className="text-2xl sm:text-3xl font-black tracking-tight text-white mb-2">
            Refiere MiA y Ahorra en tu Renovación
          </DialogTitle>
          <DialogDescription className="text-indigo-100 text-sm sm:text-base leading-relaxed">
            Por cada amigo o colega que se suscriba y adquiera un plan de pago, recibes{" "}
            <strong className="text-amber-300 font-bold">$10.000 COP de descuento</strong> en la renovación de tu suscripción del próximo mes.
          </DialogDescription>
        </div>

        <div className="p-6 sm:p-8 space-y-6">
          {/* Tarjeta de Enlace Personal */}
          <div className="p-4 sm:p-5 rounded-2xl bg-indigo-50/60 border border-indigo-100 space-y-3">
            <label className="text-xs font-bold uppercase tracking-wider text-indigo-900 flex items-center justify-between">
              <span>Tu Enlace Único de Referido</span>
              <span className="text-[11px] font-semibold text-indigo-600">Código: {referralCode}</span>
            </label>

            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                readOnly
                value={referralLink}
                className="bg-white border-indigo-200 text-slate-800 font-mono text-xs sm:text-sm h-11 rounded-xl shadow-inner select-all"
              />
              <Button
                onClick={handleCopyLink}
                className={`h-11 px-5 rounded-xl font-bold transition-all flex items-center justify-center gap-2 shrink-0 ${
                  copied
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-600/20"
                }`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "¡Copiado!" : "Copiar Enlace"}
              </Button>
            </div>

            <Button
              onClick={handleShareWhatsApp}
              variant="outline"
              className="w-full h-11 rounded-xl border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:text-emerald-900 font-bold flex items-center justify-center gap-2 shadow-sm transition-all"
            >
              <Share2 className="w-4 h-4 text-emerald-600" />
              Compartir directamente por WhatsApp
            </Button>
          </div>

          {/* Cómo Funciona */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
              <Users className="w-4 h-4 text-indigo-600" />
              ¿Cómo funciona el beneficio?
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3.5 rounded-2xl border border-slate-100 bg-slate-50/50">
                <div className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-700 font-black text-xs flex items-center justify-center mb-2">
                  1
                </div>
                <p className="text-xs font-bold text-slate-800 mb-1">Comparte tu Enlace</p>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Envía tu enlace o código a colegas, abogados o contactos de tu firma.
                </p>
              </div>

              <div className="p-3.5 rounded-2xl border border-slate-100 bg-slate-50/50">
                <div className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 font-black text-xs flex items-center justify-center mb-2">
                  2
                </div>
                <p className="text-xs font-bold text-slate-800 mb-1">Tu amigo adquiere un Plan</p>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Cuando tu referido cree su cuenta y active cualquier plan en MiA.
                </p>
              </div>

              <div className="p-3.5 rounded-2xl border border-slate-100 bg-slate-50/50">
                <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 font-black text-xs flex items-center justify-center mb-2">
                  3
                </div>
                <p className="text-xs font-bold text-slate-800 mb-1">Ahorras en Renovación</p>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Recibes $10.000 COP de descuento en tu factura del mes siguiente por cada amigo.
                </p>
              </div>
            </div>
          </div>

          {/* Simulador de Descuentos */}
          <div className="p-4 rounded-2xl bg-amber-50/80 border border-amber-200/70 flex items-start gap-3">
            <Percent className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 leading-relaxed">
              <strong className="font-bold">¡Descuento Acumulable y Sin Límites!</strong>
              <p className="mt-0.5 text-amber-800/90 text-[11px]">
                Si refieres 3 amigos que adquieran su plan, acumulas <strong>$30.000 COP</strong> de descuento. Si refieres a 5, descuentas <strong>$50.000 COP</strong> en tu siguiente renovación.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
